import { Pencil, Plus, ShieldCheck, Trash2, X } from 'lucide-react';
import { FormEvent, useEffect, useState } from 'react';
import { Loading } from '../components/Loading';
import { StatusBadge } from '../components/StatusBadge';
import { api, ApiError, dateTime } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { Admin, AdminRole } from '../lib/types';

const roleLabels: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Super administrador',
  OPERATIONS: 'Operações',
  COMPLIANCE: 'Compliance / KYC',
  SUPPORT: 'Suporte',
  AUDITOR: 'Auditor',
};

type AdminListItem = Admin & {
  active: boolean;
  lastLoginAt: string | null;
  createdAt: string;
};

export function AdminsPage() {
  const { admin: currentAdmin } = useAuth();
  const [items, setItems] = useState<AdminListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminListItem | null>(null);

  const load = () =>
    api<AdminListItem[]>('/admins')
      .then(setItems)
      .finally(() => setLoading(false));

  useEffect(() => {
    void load();
  }, []);

  async function toggle(admin: AdminListItem) {
    await api(`/admins/${admin.id}/active`, {
      method: 'PATCH',
      body: JSON.stringify({ active: !admin.active }),
    });
    await load();
  }

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Controlo de acesso</span>
          <h1>Administradores</h1>
          <p>Contas próprias do backoffice e respetivas permissões.</p>
        </div>
        <button className="button button--primary" onClick={() => setShowCreate(true)}>
          <Plus size={17} /> Novo administrador
        </button>
      </div>
      <section className="panel">
        {loading ? (
          <Loading />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Utilizador</th>
                  <th>Função</th>
                  <th>MFA</th>
                  <th>Último acesso</th>
                  <th>Estado</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {items.map((admin) => (
                  <tr key={admin.id}>
                    <td>
                      <div className="customer-cell">
                        <span className="avatar avatar--small">
                          {admin.fullName.slice(0, 2).toUpperCase()}
                        </span>
                        <div><strong>{admin.fullName}</strong><small>{admin.email}</small></div>
                      </div>
                    </td>
                    <td>{roleLabels[admin.role] ?? admin.role.replaceAll('_', ' ')}</td>
                    <td>{admin.mfaEnabled ? 'Ativo' : 'Por configurar'}</td>
                    <td>{dateTime(admin.lastLoginAt)}</td>
                    <td><StatusBadge value={admin.active ? 'ACTIVE' : 'SUSPENDED'} /></td>
                    <td>
                      <div className="admins-row-actions">
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => setEditTarget(admin)}
                          aria-label="Editar administrador"
                          title="Editar"
                        >
                          <Pencil size={16} />
                        </button>
                        <button className="table-action" onClick={() => toggle(admin)}>
                          {admin.active ? 'Desativar' : 'Ativar'}
                        </button>
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => setDeleteTarget(admin)}
                          disabled={admin.id === currentAdmin?.id}
                          aria-label="Eliminar administrador"
                          title={
                            admin.id === currentAdmin?.id
                              ? 'Não pode eliminar a sua própria conta'
                              : 'Eliminar'
                          }
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {showCreate && (
        <CreateAdminModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await load();
          }}
        />
      )}
      {editTarget && (
        <EditAdminModal
          admin={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={async () => {
            setEditTarget(null);
            await load();
          }}
        />
      )}
      {deleteTarget && (
        <DeleteAdminModal
          admin={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={async () => {
            setDeleteTarget(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function CreateAdminModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<AdminRole>('SUPPORT');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('/admins', {
        method: 'POST',
        body: JSON.stringify({ fullName, email, password, role }),
      });
      await onCreated();
    } catch {
      setError('Não foi possível criar o administrador.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <form className="modal" onSubmit={submit}>
        <div className="modal__heading">
          <div><span className="eyebrow">Acesso administrativo</span><h2>Novo administrador</h2></div>
          <button className="icon-button" type="button" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="admin-create-icon"><ShieldCheck size={24} /></div>
        <label>Nome completo<input value={fullName} onChange={(e) => setFullName(e.target.value)} required minLength={2} /></label>
        <label>Email profissional<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
        <label>Palavra-passe temporária<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={12} /></label>
        <label>Função<select value={role} onChange={(e) => setRole(e.target.value as AdminRole)}>
          <option value="SUPPORT">Suporte</option>
          <option value="OPERATIONS">Operações</option>
          <option value="COMPLIANCE">Compliance / KYC</option>
          <option value="AUDITOR">Auditor</option>
          <option value="SUPER_ADMIN">Super administrador</option>
        </select></label>
        {error && <div className="form-error">{error}</div>}
        <div className="modal__actions">
          <button type="button" className="button button--secondary" onClick={onClose}>Cancelar</button>
          <button className="button button--primary" disabled={busy}>{busy ? 'A criar…' : 'Criar conta'}</button>
        </div>
      </form>
    </div>
  );
}

function EditAdminModal({
  admin,
  onClose,
  onSaved,
}: {
  admin: AdminListItem;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [fullName, setFullName] = useState(admin.fullName);
  const [email, setEmail] = useState(admin.email);
  const [role, setRole] = useState<AdminRole>(admin.role);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api(`/admins/${admin.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ fullName, email, role }),
      });
      await onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          err.message === 'cannot_remove_last_super_admin'
            ? 'Não pode remover o último super administrador.'
            : 'Já existe uma conta com este email.',
        );
      } else {
        setError('Não foi possível guardar as alterações.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <form className="modal" onSubmit={submit}>
        <div className="modal__heading">
          <div><span className="eyebrow">Acesso administrativo</span><h2>Editar administrador</h2></div>
          <button className="icon-button" type="button" onClick={onClose}><X size={20} /></button>
        </div>
        <label>Nome completo<input value={fullName} onChange={(e) => setFullName(e.target.value)} required minLength={2} maxLength={120} /></label>
        <label>Email profissional<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
        <label>Função<select value={role} onChange={(e) => setRole(e.target.value as AdminRole)}>
          <option value="SUPPORT">Suporte</option>
          <option value="OPERATIONS">Operações</option>
          <option value="COMPLIANCE">Compliance / KYC</option>
          <option value="AUDITOR">Auditor</option>
          <option value="SUPER_ADMIN">Super administrador</option>
        </select></label>
        {error && <div className="form-error">{error}</div>}
        <div className="modal__actions">
          <button type="button" className="button button--secondary" onClick={onClose}>Cancelar</button>
          <button className="button button--primary" disabled={busy}>{busy ? 'A guardar…' : 'Guardar alterações'}</button>
        </div>
      </form>
    </div>
  );
}

function DeleteAdminModal({
  admin,
  onClose,
  onDeleted,
}: {
  admin: AdminListItem;
  onClose: () => void;
  onDeleted: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function confirmDelete() {
    setBusy(true);
    setError('');
    try {
      await api(`/admins/${admin.id}`, { method: 'DELETE' });
      await onDeleted();
    } catch (err) {
      if (err instanceof ApiError && err.message === 'admin_has_kyc_history') {
        setError(
          'Este administrador tem decisões KYC associadas e não pode ser eliminado. Desative a conta em vez disso.',
        );
      } else {
        setError('Não foi possível eliminar o administrador.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <div className="modal">
        <div className="modal__heading">
          <div><span className="eyebrow">Acesso administrativo</span><h2>Eliminar administrador?</h2></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar"><X size={20} /></button>
        </div>
        <p>
          Esta ação é irreversível. <strong>{admin.fullName}</strong> ({admin.email}) perde
          imediatamente o acesso ao backoffice.
        </p>
        {error && <div className="form-error">{error}</div>}
        <div className="modal__actions">
          <button type="button" className="button button--secondary" onClick={onClose}>Cancelar</button>
          <button className="button button--danger" onClick={confirmDelete} disabled={busy}>
            {busy ? 'A eliminar…' : 'Eliminar administrador'}
          </button>
        </div>
      </div>
    </div>
  );
}
